import { Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { AddLogSourceSkeleton } from '@/features/integrations/collectors/components/skeletons'
import { collectorQueries } from '@/features/integrations/collectors/queries'
import { integrationQueries } from '@/features/integrations/queries'
import { lazyNamed } from '@/utils/lazy-named'

import { ROLES } from '@/shared/auth/constants'
import { getCurrentTenant } from '@/shared/auth/store'
import { roleAuthGuard } from '@/shared/auth/utils/router'

import { ErrorBoundary } from '@/components/common/error-boundary'

/**
 * Loaded on intent, not eagerly (P-21): the wizard is reached only by clicking a
 * Network Logs catalog card, which preloads the chunk on hover.
 */
const AddLogSourceWizard = lazyNamed(
	() =>
		import('@/features/integrations/collectors/components/add-log-source/wizard'),
	'AddLogSourceWizard',
)

export const Route = createFileRoute(
	'/_dashboard/integrations/collectors/sources/new/$definitionId',
)({
	// The same bar `/integrations/new` enforces and the card that links here sits
	// behind (D34): a gate on the affordance alone leaves the URL open.
	beforeLoad: () => roleAuthGuard(ROLES.SystemManager),

	// Both reads the wizard suspends on (A-07). The Log Source Type comes from
	// the catalog rather than a by-id definition read — see the wizard's doc.
	loader: ({ context: { queryClient } }) => {
		const tenant = getCurrentTenant()
		if (!tenant) return

		queryClient.prefetchQuery(integrationQueries.available(tenant))
		queryClient.prefetchQuery(collectorQueries.list(tenant))
	},

	head: () => ({
		meta: [{ title: 'Add Log Source | Integrations | BitLyft AIR®' }],
	}),

	component: AddLogSourcePage,
	pendingComponent: AddLogSourceSkeleton,
	errorComponent: RouteErrorComponent,
})

function AddLogSourcePage() {
	const { definitionId } = Route.useParams()

	return (
		<ErrorBoundary
			feature="integrations"
			section="add-log-source"
			entityId={definitionId}
		>
			<Suspense fallback={<AddLogSourceSkeleton />}>
				<AddLogSourceWizard definitionId={definitionId} />
			</Suspense>
		</ErrorBoundary>
	)
}
